"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useAuth } from "@/contexts/AuthContext";
import Icon from "./ui/AppIcon";
import AppLogo from "./ui/AppLogo";

export default function Sidebar() {
    const pathname = usePathname();
    const { user, profile, signOut } = useAuth();
    const isAdmin = user?.user_metadata?.role === "admin" || user?.app_metadata?.role === "admin";

    // Shared links
    const sharedLinks = isAdmin ? [] : [
        { label: "Dashboard", href: "/dashboard", icon: "HomeIcon" },
    ];

    // User-specific links
    const userLinks = [
        { label: "Search Database", href: "/search-page", icon: "MagnifyingGlassIcon" },
        { label: "Add Transaction", href: "/add-transaction-page", icon: "PlusIcon" },
        { label: "Subscription", href: "/subscription", icon: "CreditCardIcon" },
        { label: "Billing History", href: "/billing", icon: "DocumentTextIcon" },
    ];

    // Admin-specific links
    const adminLinks = [
        { label: "Admin Dashboard", href: "/admin-dashboard", icon: "ChartBarIcon" },
        { label: "User Management", href: "/admin-dashboard/users", icon: "UsersIcon" },
        { label: "Manage Database", href: "/search-page", icon: "CircleStackIcon" },
        { label: "System Pricing", href: "/subscription", icon: "Cog6ToothIcon" },
    ];

    const links = isAdmin ? [...sharedLinks, ...adminLinks] : [...sharedLinks, ...userLinks];

    return (
        <aside id="app-sidebar" className="fixed left-0 top-0 h-screen w-64 bg-navy text-white z-50 overflow-y-auto border-r border-white/10 lg:flex flex-col transition-transform duration-300 ease-in-out -translate-x-full lg:translate-x-0">
            {/* Sidebar Header */}
            <div className="p-6 border-b border-white/10">
                <Link href="/" className="flex items-center gap-2.5 group">
                    <div className="bg-white/10 p-1.5 rounded-lg group-hover:bg-teal transition-colors">
                        <AppLogo size={28} />
                    </div>
                    <div>
                        <span className="font-heading font-700 text-white text-base leading-tight tracking-tight block">
                            PropertyData<span className="text-teal">TT</span>
                        </span>
                        <span className="text-[10px] text-white/40 leading-none tracking-widest uppercase font-body mt-1 block">
                            {isAdmin ? "Admin Console" : "Agent Portal"}
                        </span>
                    </div>
                </Link>
            </div>

            {/* Navigation */}
            <nav className="flex-1 px-4 py-8 space-y-2">
                <p className="px-3 mb-4 text-[10px] font-bold uppercase tracking-[0.2em] text-white/30">
                    Main Navigation
                </p>
                {links.map((link) => {
                    const isActive = pathname === link.href;
                    return (
                        <Link
                            key={link.label}
                            href={link.href}
                            className={`
                flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200
                ${isActive
                                    ? "bg-teal text-white shadow-lg shadow-teal/20"
                                    : "text-white/60 hover:text-white hover:bg-white/5"}
              `}
                        >
                            <Icon name={link.icon as any} size={18} className={isActive ? "text-white" : "text-teal"} />
                            {link.label}
                        </Link>
                    );
                })}
            </nav>

            {/* Account Sidebar Footer */}
            <div className="p-6 mt-auto border-t border-white/10 bg-white/5">
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-teal flex items-center justify-center font-bold text-navy shadow-inner">
                        {profile?.full_name?.[0].toUpperCase() || "U"}
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold truncate">{profile?.full_name || "User"}</p>
                        <p className="text-xs text-white/40 truncate capitalize">
                            {isAdmin ? "Global Admin" : "Licensed Agent"}
                        </p>
                    </div>
                </div>

                <Link
                    href="/terms-page"
                    className="flex items-center gap-2 text-xs text-white/40 hover:text-teal transition-colors mb-2"
                >
                    <Icon name="InformationCircleIcon" size={14} />
                    Terms & Disclaimer
                </Link>

                <button
                    onClick={() => signOut()}
                    className="w-full flex items-center gap-2 text-xs text-white/40 hover:text-red-400 transition-colors"
                >
                    <Icon name="ArrowRightOnRectangleIcon" size={14} />
                    Log Out
                </button>
            </div>
        </aside>
    );
}
